import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PsychologyRounded from '@mui/icons-material/PsychologyRounded';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import ReplyRounded from '@mui/icons-material/ReplyRounded';
import { BarList, EmptyState, MetricCard, Panel, type MetricTrend } from '../../../ui-kit';
import { fetchPsSpotlight, type PsDay, type PsSpotlight } from '../../../services/policyServicingService';
import AgentFlow, { type FlowNode } from '../rocco/AgentFlow';
import { KlarisMark } from '../rocco/IntegrationPicker';
import { useLive } from '../rocco/live';
import { MailboxLogo } from './logos';
import { useAgent } from '../AgentContext';

const whole = (v: number) => v.toLocaleString();

/** What the page draws before the figures arrive: every part present, all empty. */
const EMPTY: PsSpotlight = {
  flow: {
    mailbox: { address: null, provider: null, checked_at: null, error: null },
    today: { received: 0, understood: 0, policies_found: 0, calls: 0, replied: 0 },
  },
  tiles: {
    received: [], replied: [], needs_person: [], waiting_approval: 0,
    totals: { answered: 0, carrier_calls: 0, needs_person: 0, in_progress: 0, median_minutes_to_reply: null },
  },
  by_type: [],
  handed_over: [],
};

/**
 * The page the Policy Servicing agent opens on. Every figure is read from the
 * running system.
 *
 * Top to bottom: how an email moves through it (each step saying what it did
 * today), the week in numbers, then what customers ask about and why some
 * go to a person.
 */
export default function SpotlightPage() {
  const navigate = useNavigate();
  const { agent, base } = useAgent();
  const fetcher = React.useCallback(() => fetchPsSpotlight(agent.id), [agent.id]);
  const { data, error } = useLive(fetcher, 10_000);

  if (error && !data) return <EmptyState message={error} />;
  const loading = !data;
  const { flow, tiles } = data ?? EMPTY;
  const totals = tiles.totals;
  const { mailbox } = flow;
  const setup = data?.setup;
  const emailsCarriers = setup?.carrier_channel === 'email';

  // How the agent works, step by step: what each step does, not how much it did.
  // The day's figures are the tiles below.
  const nodes: FlowNode[] = [
    {
      id: 'inbox',
      step: 'Emails in',
      name: mailbox.address ? 'Service inbox' : 'No mailbox connected',
      detail: !mailbox.address ? 'Connect one under Integrations'
        : mailbox.error ? `${mailbox.address} · could not be reached` : mailbox.address,
      logo: <MailboxLogo provider={mailbox.provider} />,
      to: 'integrations',
    },
    {
      id: 'understand',
      step: 'Understands',
      name: 'Reads the request',
      detail: 'Which question, which policy',
      logo: <PsychologyRounded sx={{ fontSize: 20 }} />,
      to: 'configuration',
    },
    {
      id: 'policy',
      step: 'Looks up',
      name: 'Finds the policy',
      detail: setup?.policy_system ? `In ${setup.policy_system}` : 'Only for the policyholder on record',
      logo: <PolicyRounded sx={{ fontSize: 20 }} />,
      to: 'configuration',
    },
    emailsCarriers ? {
      id: 'dialer',
      step: 'Asks the carrier',
      name: 'By email',
      detail: setup?.answer_from_records ? 'When the policy records do not say' : 'Asks for the answer',
      logo: <MailboxLogo provider={mailbox.provider} />,
      to: 'completed',
    } : {
      id: 'dialer',
      step: 'Calls the carrier',
      name: 'Klaris AI Dialer',
      detail: 'Asks for the answer',
      logo: <KlarisMark size={22} />,
      to: 'completed',
    },
    {
      id: 'reply',
      step: 'Replies',
      name: 'Answers the customer',
      detail: 'In the same email thread',
      logo: <ReplyRounded sx={{ fontSize: 20 }} />,
      to: 'completed',
    },
  ];

  const trend = (points: PsDay[], label: string): MetricTrend => ({
    points: points.map((p, i) => ({
      key: p.day,
      label: i === points.length - 1 ? 'Today'
        : new Date(`${p.day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
      value: p.value,
    })),
    formatValue: whole,
    label,
  });

  return (
    <Stack spacing={2}>
      <AgentFlow
        nodes={nodes}
        loading={loading}
        subtitle="From a customer's email to the carrier's answer in their inbox"
        onOpen={(to) => navigate(`${base}/${to}`)}
      />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
        <MetricCard loading={loading} label="Questions answered by AI" value={whole(totals.answered)}
          info="Customers who got their answer in their own email thread, without anyone picking up the phone."
          note={totals.answered_from_records
            ? `${whole(totals.answered_from_records)} from our own records, ${emailsCarriers ? 'no carrier asked' : 'with no call'}`
            : undefined}
          trend={trend(tiles.replied, 'Answered by day, this week')} />
        <MetricCard loading={loading} label="Typical AI reply time"
          value={totals.median_minutes_to_reply == null ? '—' : minutes(totals.median_minutes_to_reply)}
          info="From the customer's email arriving to our reply going out. This is the usual wait, not the best one: half of requests are answered sooner, half later. Done by hand this takes days." />
        {emailsCarriers ? (
          <MetricCard loading={loading} label="Carriers emailed" value={whole(totals.carriers_emailed ?? 0)}
            info="Questions the policy records could not answer, written to the carrier's service team from this inbox." />
        ) : (
          <MetricCard loading={loading} label="Carrier calls made by AI" value={whole(totals.carrier_calls)}
            info="Calls the agent placed to carriers to get the answer — the part nobody wants to do." />
        )}
        <MetricCard loading={loading} label="Needed a person" value={whole(totals.needs_person)}
          info="Requests the agent would not finish alone, each with its reason. It should be a small number."
          trend={trend(tiles.needs_person, 'Handed to a person by day, this week')} />
        <MetricCard loading={loading} label="Being worked on by AI" value={whole(totals.in_progress)}
          info={`Requests in flight right now, ${whole(tiles.waiting_approval)} of them waiting for approval.`} />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <Panel title="What customers ask" subtitle="Every request it has read" loading={loading} loadingHeight={180}>
          {data?.by_type.length ? (
            <BarList
              rows={data.by_type.map((row) => ({ key: row.key, label: row.label, value: row.value }))}
              formatValue={whole}
              valueLabel="Requests"
              labelHeading="Request"
            />
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>No requests yet.</Typography>
          )}
        </Panel>
        <Panel title="Why some went to a person" subtitle="Every one that was handed over" loading={loading} loadingHeight={180}>
          {data?.handed_over.length ? (
            <BarList
              rows={data.handed_over.map((row) => ({ key: row.label, label: row.label, value: row.value }))}
              formatValue={whole}
              valueLabel="Requests"
              labelHeading="Reason"
            />
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              None so far: the agent has finished every request itself.
            </Typography>
          )}
        </Panel>
      </Box>
    </Stack>
  );
}

function minutes(value: number): string {
  if (value < 60) return `${value} min`;
  const h = Math.floor(value / 60);
  const m = value % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
